import { useCallback, useEffect, useState } from "react";
import { Download, Info, Loader2, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { cn } from "../../lib/utils";
import {
  WaterfallRow,
  WaterfallResult,
  WaterfallTotals,
} from "../../lib/cashflow-waterfall";
import {
  buildConsolidatedCashflowApiParams,
  type ConsolidatedFilters,
} from "../../lib/consolidated-cashflow";
import apiService from "../../services/apiService";
import { CashflowGlossarySheet } from "./CashflowGlossarySheet";
import { useSidebar } from "../../context/SidebarContext";

interface Props {
  /** When set, fetches cashflow for a single rental schedule; omit for consolidated portfolio view. */
  rentalScheduleId?: string;
  /** Used for CSV export filename (e.g. display id). */
  exportLabel?: string;
  /** Hide assumption badges / CSV / expand toolbar (e.g. when embedded under consolidated filters). */
  showToolbar?: boolean;
  /** Optional height class for the table container. */
  className?: string;
  /** Portfolio consolidated view: Active RS column, no assignment status. */
  variant?: "schedule" | "portfolio";
  /** Filters for consolidated portfolio cashflow API (granularity, fy, customerIds). */
  consolidatedFilters?: ConsolidatedFilters;
  /** Notifies parent of loaded data row count (excludes totals row). */
  onRowCountChange?: (count: number) => void;
}

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

const EMPTY_ASSUMPTIONS: WaterfallResult["assumptions"] = {
  baseAssetValue: 0,
  totalAssetValue: 0,
  assetItc: 0,
  sd: 0,
  pfBase: 0,
  pfGst: 0,
  pfTotal: 0,
  lenderDisbursement: 0,
  residual: 0,
  tenureMonths: 0,
  commencementDate: "",
};

const EMPTY_TOTALS: WaterfallTotals = {
  customerPays: 0,
  netCashFlow: 0,
  astraToDate: 0,
  astraLifetime: 0,
  lenderLifetime: 0,
  capitalDeployed: 0,
  capitalRecovered: 0,
  revenueBookedToDate: 0,
  revenueThisTranche: 0,
  itcUsedTotal: 0,
  tdsTotal: 0,
};

const num = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const pick = (obj: Record<string, unknown>, camel: string, snake: string) =>
  obj[camel] ?? obj[snake];

const normalizeWaterfallRow = (raw: Record<string, unknown>): WaterfallRow => ({
  monthDate: String(pick(raw, "monthDate", "month_date") ?? ""),
  monthLabel: String(pick(raw, "monthLabel", "month_label") ?? ""),
  event: String(
    pick(raw, "event") ??
      pick(raw, "activeRS", "active_rs") ??
      ""
  ),
  eventType: (pick(raw, "eventType", "event_type") ?? "Rent") as WaterfallRow["eventType"],
  assigned:
    raw.assigned === null || raw.assigned === undefined
      ? null
      : Boolean(raw.assigned),
  customerPays: num(pick(raw, "customerPays", "customer_pays")),
  sdInOut: num(pick(raw, "sdInOut", "sd_in_out")),
  pfIn: num(pick(raw, "pfIn", "pf_in")),
  lenderIn: num(pick(raw, "lenderIn", "lender_in")),
  vendorPay: num(pick(raw, "vendorPay", "vendor_pay")),
  outputGST: num(pick(raw, "outputGST", "output_gst")),
  itcBF: num(pick(raw, "itcBF", "itc_bf")),
  itcUsed: num(pick(raw, "itcUsed", "itc_used")),
  itcCF: num(pick(raw, "itcCF", "itc_cf")),
  gstPaid: num(pick(raw, "gstPaid", "gst_paid")),
  tds: num(raw.tds),
  cmTds: num(pick(raw, "cmTds", "cm_tds")),
  netCashFlow: num(pick(raw, "netCashFlow", "net_cash_flow")),
  cashflowToAstrafin: num(pick(raw, "cashflowToAstrafin", "cashflow_to_astrafin")),
  cmCashflowToAstrafin: num(pick(raw, "cmCashflowToAstrafin", "cm_cashflow_to_astrafin")),
  cashflowToLender: num(pick(raw, "cashflowToLender", "cashflow_to_lender")),
  isPast: Boolean(pick(raw, "isPast", "is_past")),
});

const normalizeWaterfallTotals = (raw: Record<string, unknown>): WaterfallTotals => ({
  customerPays: num(pick(raw, "customerPays", "customer_pays")),
  netCashFlow: num(pick(raw, "netCashFlow", "net_cash_flow")),
  astraToDate: num(pick(raw, "astraToDate", "astra_to_date")),
  astraLifetime: num(pick(raw, "astraLifetime", "astra_lifetime")),
  lenderLifetime: num(pick(raw, "lenderLifetime", "lender_lifetime")),
  capitalDeployed: num(pick(raw, "capitalDeployed", "capital_deployed")),
  capitalRecovered: num(pick(raw, "capitalRecovered", "capital_recovered")),
  revenueBookedToDate: num(pick(raw, "revenueBookedToDate", "revenue_booked_to_date")),
  revenueThisTranche: num(pick(raw, "revenueThisTranche", "revenue_this_tranche")),
  itcUsedTotal: num(pick(raw, "itcUsedTotal", "itc_used_total")),
  tdsTotal: num(pick(raw, "tdsTotal", "tds_total")),
});

const normalizeAssumptions = (
  raw: Record<string, unknown> | undefined
): WaterfallResult["assumptions"] => {
  if (!raw) return EMPTY_ASSUMPTIONS;
  return {
    baseAssetValue: num(pick(raw, "baseAssetValue", "base_asset_value")),
    totalAssetValue: num(pick(raw, "totalAssetValue", "total_asset_value")),
    assetItc: num(pick(raw, "assetItc", "asset_itc")),
    sd: num(raw.sd),
    pfBase: num(pick(raw, "pfBase", "pf_base")),
    pfGst: num(pick(raw, "pfGst", "pf_gst")),
    pfTotal: num(pick(raw, "pfTotal", "pf_total")),
    lenderDisbursement: num(pick(raw, "lenderDisbursement", "lender_disbursement")),
    residual: num(raw.residual),
    tenureMonths: num(pick(raw, "tenureMonths", "tenure_months")),
    commencementDate: String(pick(raw, "commencementDate", "commencement_date") ?? ""),
  };
};

const normalizeCashflowResponse = (payload: unknown): WaterfallResult => {
  const root =
    payload && typeof payload === "object" && "data" in payload
      ? (payload as ApiResponse<unknown>).data
      : payload;

  if (!root || typeof root !== "object") {
    return { rows: [], totals: EMPTY_TOTALS, assumptions: EMPTY_ASSUMPTIONS };
  }

  const body = root as Record<string, unknown>;
  const rawRows = Array.isArray(body.rows) ? body.rows : [];
  const rawTotals =
    body.totals && typeof body.totals === "object"
      ? (body.totals as Record<string, unknown>)
      : {};
  const rawAssumptions =
    body.assumptions && typeof body.assumptions === "object"
      ? (body.assumptions as Record<string, unknown>)
      : undefined;

  return {
    rows: rawRows.map((row) =>
      normalizeWaterfallRow(row as Record<string, unknown>)
    ),
    totals: normalizeWaterfallTotals(rawTotals),
    assumptions: normalizeAssumptions(rawAssumptions),
  };
};

export const normalizeCashflowApiResponse = normalizeCashflowResponse;

export const buildPortfolioCashflowSheetData = (
  rows: WaterfallRow[],
  totals: WaterfallTotals
): (string | number)[][] => {
  const lastRow = rows[rows.length - 1];
  const headers = [
    "Month",
    "Active RS",
    "Customer Pays",
    "Security Deposit Movement",
    "PF In",
    "Lender Disbursement",
    "Vendor Payment",
    "Output Tax Liability",
    "Opening ITC",
    "ITC Utilised",
    "Closing ITC",
    "Net GST Payable",
    "TDS Withheld",
    "Cumulative TDS Withheld",
    "Cashflow",
    "Revenue to Astrafin",
    "Cumulative Astrafin Revenue",
    "Lender Pay-through",
  ];

  return [
    headers,
    ...rows.map((r) => [
      r.monthLabel,
      r.event,
      Math.round(r.customerPays),
      Math.round(r.sdInOut),
      Math.round(r.pfIn),
      Math.round(r.lenderIn),
      Math.round(r.vendorPay),
      Math.round(r.outputGST),
      Math.round(r.itcBF),
      Math.round(r.itcUsed),
      Math.round(r.itcCF),
      Math.round(r.gstPaid),
      Math.round(r.tds),
      Math.round(r.cmTds),
      Math.round(r.netCashFlow),
      Math.round(r.cashflowToAstrafin),
      Math.round(r.cmCashflowToAstrafin),
      Math.round(r.cashflowToLender),
    ]),
    [
      "Totals",
      "",
      Math.round(totals.customerPays),
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      Math.round(totals.tdsTotal),
      Math.round(lastRow?.cmTds ?? 0),
      Math.round(totals.netCashFlow),
      Math.round(totals.astraLifetime),
      Math.round(lastRow?.cmCashflowToAstrafin ?? 0),
      Math.round(totals.lenderLifetime),
    ],
  ];
};

const fmt = (n: number) => {
  if (n === 0) return "0";
  const abs = Math.abs(Math.round(n)).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return n < 0 ? `(${abs})` : abs;
};

const cellTone = (n: number) => (n < 0 ? "text-red-500" : n > 0 ? "" : "text-muted-foreground");

const COL_PAD = "px-3 py-2.5";
const MONTH_STICKY_LEFT = "left-0 min-w-[7.5rem]";
const SECOND_STICKY_LEFT = "left-[7.5rem] min-w-[6.5rem]";
const NUM_COL = `${COL_PAD} text-right tabular-nums min-w-[7.5rem] whitespace-nowrap`;
const NUM_COL_WIDE = `${COL_PAD} text-right tabular-nums min-w-[9.5rem] whitespace-nowrap`;
const HEAD_COL = `${COL_PAD} whitespace-nowrap`;
const HEAD_NUM = `${COL_PAD} text-right whitespace-nowrap min-w-[7.5rem]`;
const HEAD_NUM_WIDE = `${COL_PAD} text-right whitespace-nowrap min-w-[9.5rem]`;
const PAST_ROW_BG = "bg-slate-100";
const FUTURE_ROW_BG = "bg-white";
const TAX_OUTFLOW_TEXT = "text-red-500";

const TableInner = ({
  rows,
  totals,
  variant = "schedule",
}: {
  rows: WaterfallRow[];
  totals: WaterfallTotals;
  variant?: "schedule" | "portfolio";
}) => {
  const isPortfolio = variant === "portfolio";
  const leadingColSpan = isPortfolio ? 2 : 3;

  return (
  <div className="h-full overflow-x-auto overflow-y-auto rounded-md border">
    <Table className="text-s min-w-max w-full">
      <TableHeader className="sticky top-0 z-30 bg-white backdrop-blur">
        <TableRow>
          <TableHead className={cn("sticky z-30 bg-white backdrop-blur", HEAD_COL, MONTH_STICKY_LEFT)}>Month</TableHead>
          <TableHead className={cn("sticky z-30 bg-white backdrop-blur", HEAD_COL, SECOND_STICKY_LEFT, isPortfolio && "text-center")}>
            {isPortfolio ? "Active RS" : "Event"}
          </TableHead>
          {!isPortfolio && (
            <TableHead className={cn(HEAD_COL, "text-center min-w-[7rem]")}>Assignment Status</TableHead>
          )}
          <TableHead className={HEAD_NUM}>Customer Pays</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Security Deposit Movement</TableHead>
          <TableHead className={HEAD_NUM}>PF In</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Lender Disbursement</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Vendor Payment</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Output Tax Liability</TableHead>
          <TableHead className={HEAD_NUM}>Opening ITC</TableHead>
          <TableHead className={HEAD_NUM}>ITC Utilised</TableHead>
          <TableHead className={HEAD_NUM}>Closing ITC</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Net GST Payable</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>TDS Withheld</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Cumulative TDS Withheld</TableHead>
          <TableHead className={HEAD_NUM}>Cashflow</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Revenue to Astrafin</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Cumulative Astrafin Revenue</TableHead>
          <TableHead className={HEAD_NUM_WIDE}>Lender Pay-through</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => {
          const isAccent =
            row.eventType === "Inception" ||
            row.eventType === "SD Refund" ||
            row.eventType === "Final GST";
          const pastBg = row.isPast ? PAST_ROW_BG : FUTURE_ROW_BG;
          const stickyBg = row.isPast ? PAST_ROW_BG : FUTURE_ROW_BG;
          return (
            <TableRow
              key={i}
              className={cn(
                pastBg,
                row.isPast && "text-slate-700 hover:bg-slate-100",
                isAccent && "font-medium",
              )}
            >
              <TableCell className={cn("sticky backdrop-blur", COL_PAD, MONTH_STICKY_LEFT, stickyBg)}>
                {row.monthLabel}
              </TableCell>
              <TableCell className={cn("sticky backdrop-blur", COL_PAD, SECOND_STICKY_LEFT, stickyBg, isPortfolio && "text-center tabular-nums")}>
                {row.event}
              </TableCell>
              {!isPortfolio && (
                <TableCell className={cn(COL_PAD, "text-center min-w-[7rem]")}>
                  {row.assigned === null ? "—" : row.assigned ? "Y" : "N"}
                </TableCell>
              )}
              <TableCell className={NUM_COL}>{fmt(row.customerPays)}</TableCell>
              <TableCell className={cn(NUM_COL_WIDE, cellTone(row.sdInOut))}>{fmt(row.sdInOut)}</TableCell>
              <TableCell className={NUM_COL}>{fmt(row.pfIn)}</TableCell>
              <TableCell className={NUM_COL_WIDE}>{fmt(row.lenderIn)}</TableCell>
              <TableCell className={cn(NUM_COL_WIDE, cellTone(row.vendorPay))}>{fmt(row.vendorPay)}</TableCell>
              <TableCell className={NUM_COL_WIDE}>{fmt(row.outputGST)}</TableCell>
              <TableCell className={NUM_COL}>{fmt(row.itcBF)}</TableCell>
              <TableCell className={NUM_COL}>{fmt(row.itcUsed)}</TableCell>
              <TableCell className={NUM_COL}>{fmt(row.itcCF)}</TableCell>
              <TableCell className={cn(NUM_COL_WIDE, TAX_OUTFLOW_TEXT)}>
                {fmt(row.gstPaid)}
              </TableCell>
              <TableCell className={cn(NUM_COL_WIDE, TAX_OUTFLOW_TEXT)}>
                {fmt(row.tds)}
              </TableCell>
              <TableCell className={cn(NUM_COL_WIDE, TAX_OUTFLOW_TEXT)}>
                {fmt(row.cmTds)}
              </TableCell>
              <TableCell className={cn(NUM_COL, "font-medium", cellTone(row.netCashFlow))}>
                {fmt(row.netCashFlow)}
              </TableCell>
              <TableCell className={cn(NUM_COL_WIDE, "font-medium", cellTone(row.cashflowToAstrafin))}>
                {fmt(row.cashflowToAstrafin)}
              </TableCell>
              <TableCell className={cn(NUM_COL_WIDE, cellTone(row.cmCashflowToAstrafin))}>
                {fmt(row.cmCashflowToAstrafin)}
              </TableCell>
              <TableCell className={NUM_COL_WIDE}>{fmt(row.cashflowToLender)}</TableCell>
            </TableRow>
          );
        })}
        <TableRow className="bg-muted/60 font-semibold sticky bottom-0">
          <TableCell colSpan={leadingColSpan} className={cn("sticky bg-muted/60", COL_PAD, MONTH_STICKY_LEFT)}>Totals</TableCell>
          <TableCell className={NUM_COL}>{fmt(totals.customerPays)}</TableCell>
          <TableCell colSpan={9} />
          <TableCell className={cn(NUM_COL_WIDE, TAX_OUTFLOW_TEXT)}>
            {fmt(totals.tdsTotal)}
          </TableCell>
          <TableCell className={cn(NUM_COL_WIDE, TAX_OUTFLOW_TEXT)}>
            {fmt(rows.length ? rows[rows.length - 1].cmTds : 0)}
          </TableCell>
          <TableCell className={cn(NUM_COL, cellTone(totals.netCashFlow))}>
            {fmt(totals.netCashFlow)}
          </TableCell>
          <TableCell className={cn(NUM_COL_WIDE, cellTone(totals.astraLifetime))}>
            {fmt(totals.astraLifetime)}
          </TableCell>
          <TableCell className={cn(NUM_COL_WIDE, cellTone(totals.astraLifetime))}>
            {fmt(rows.length ? rows[rows.length - 1].cmCashflowToAstrafin : 0)}
          </TableCell>
          <TableCell className={NUM_COL_WIDE}>{fmt(totals.lenderLifetime)}</TableCell>
        </TableRow>

      </TableBody>
    </Table>
  </div>
  );
};

export const CashflowForecastTable = ({
  rentalScheduleId,
  exportLabel,
  showToolbar = true,
  className,
  variant = "schedule",
  consolidatedFilters,
  onRowCountChange,
}: Props) => {
  const { sidebarWidth } = useSidebar();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<WaterfallRow[]>([]);
  const [totals, setTotals] = useState<WaterfallTotals>(EMPTY_TOTALS);
  const [assumptions, setAssumptions] = useState<WaterfallResult["assumptions"]>(EMPTY_ASSUMPTIONS);

  const loadCashflow = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (rentalScheduleId) {
        const url = `/rental-schedule/get-rental-schedule-cashflow/${encodeURIComponent(rentalScheduleId)}`;
        const response = await apiService.get<unknown>(url);
        const parsed = normalizeCashflowResponse(response);
        setRows(parsed.rows);
        setTotals(parsed.totals);
        setAssumptions(parsed.assumptions);
        onRowCountChange?.(parsed.rows.length);
        return;
      }

      const params = consolidatedFilters
        ? buildConsolidatedCashflowApiParams(consolidatedFilters)
        : undefined;
      const response = await apiService.get<unknown>(
        "/rental-schedule/get-consolidated-rental-schedule-cashflow",
        params ? { params } : {}
      );
      const parsed = normalizeCashflowResponse(response);
      setRows(parsed.rows);
      setTotals(parsed.totals);
      setAssumptions(parsed.assumptions);
      onRowCountChange?.(parsed.rows.length);
    } catch (err) {
      console.error("Failed to load consolidated rental schedule cashflow:", err);
      setRows([]);
      setTotals(EMPTY_TOTALS);
      setAssumptions(EMPTY_ASSUMPTIONS);
      onRowCountChange?.(0);
      setError("Could not load cashflow forecast.");
    } finally {
      setLoading(false);
    }
  }, [rentalScheduleId, consolidatedFilters, onRowCountChange]);

  useEffect(() => {
    void loadCashflow();
  }, [loadCashflow]);

  const handleExport = () => {
    const headers = variant === "portfolio"
      ? [
          "Month","Active RS","Customer Pays","Security Deposit Movement","PF In","Lender Disbursement",
          "Vendor Payment","Output Tax Liability","Opening ITC","ITC Utilised","Closing ITC","Net GST Payable","TDS Withheld","Cumulative TDS Withheld",
          "Cashflow","Revenue to Astrafin","Cumulative Astrafin Revenue","Lender Pay-through",
        ]
      : [
          "Month","Event","Assignment Status","Customer Pays","Security Deposit Movement","PF In","Lender Disbursement",
          "Vendor Payment","Output Tax Liability","Opening ITC","ITC Utilised","Closing ITC","Net GST Payable","TDS Withheld","Cumulative TDS Withheld",
          "Cashflow","Revenue to Astrafin","Cumulative Astrafin Revenue","Lender Pay-through",
        ];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push(
        variant === "portfolio"
          ? [
              r.monthLabel, r.event,
              r.customerPays, r.sdInOut, r.pfIn, r.lenderIn, r.vendorPay,
              r.outputGST, r.itcBF, r.itcUsed, r.itcCF, r.gstPaid, r.tds, r.cmTds,
              r.netCashFlow, r.cashflowToAstrafin, r.cmCashflowToAstrafin, r.cashflowToLender,
            ].join(",")
          : [
              r.monthLabel, r.event, r.assigned === null ? "" : r.assigned ? "Y" : "N",
              r.customerPays, r.sdInOut, r.pfIn, r.lenderIn, r.vendorPay,
              r.outputGST, r.itcBF, r.itcUsed, r.itcCF, r.gstPaid, r.tds, r.cmTds,
              r.netCashFlow, r.cashflowToAstrafin, r.cmCashflowToAstrafin, r.cashflowToLender,
            ].join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cashflow-${exportLabel || rentalScheduleId || "consolidated"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const Toolbar = (
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Badge variant="secondary">Tenure {assumptions.tenureMonths}m</Badge>
        <Badge variant="outline">Asset ITC ₹{assumptions.assetItc.toLocaleString("en-IN")}</Badge>
        <Badge variant="outline">Lender Disb. ₹{assumptions.lenderDisbursement.toLocaleString("en-IN")}</Badge>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">Grey rows = past (settled); default = future forecast</span>
      </div>
      <div className="flex items-center gap-2">
        <CashflowGlossarySheet />
        <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5" disabled={loading || rows.length === 0}>
          <Download className="h-3.5 w-3.5" /> CSV
        </Button>
        <Button variant="outline" size="sm" onClick={() => setExpanded((v) => !v)} className="gap-1.5" disabled={loading}>
          {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          {expanded ? "Collapse" : "Expand"}
        </Button>
      </div>
    </div>
  );

  const body = loading ? (
    <div className="flex h-[65vh] items-center justify-center text-muted-foreground gap-2">
      <Loader2 className="h-5 w-5 animate-spin" />
      Loading cashflow forecast…
    </div>
  ) : error ? (
    <div className="flex h-[65vh] flex-col items-center justify-center gap-3 text-muted-foreground">
      <p className="flex items-center gap-2 text-destructive">
        <Info className="h-4 w-4" />
        {error}
      </p>
      <Button variant="outline" size="sm" onClick={() => void loadCashflow()}>
        Retry
      </Button>
    </div>
  ) : (
    <div className={cn("h-[65vh] min-w-0", className)}>
      <TableInner rows={rows} totals={totals} variant={variant} />
    </div>
  );

  if (!showToolbar) {
    return (
      <>
        {loading ? (
          <div className={cn("flex h-[65vh] items-center justify-center text-muted-foreground gap-2", className)}>
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading cashflow forecast…
          </div>
        ) : error ? (
          <div className={cn("flex h-[65vh] flex-col items-center justify-center gap-3 text-muted-foreground", className)}>
            <p className="flex items-center gap-2 text-destructive">
              <Info className="h-4 w-4" />
              {error}
            </p>
            <Button variant="outline" size="sm" onClick={() => void loadCashflow()}>
              Retry
            </Button>
          </div>
        ) : (
          body
        )}
      </>
    );
  }

  return (
    <>
      <div>
        {Toolbar}
        {body}
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          className="max-w-none sm:max-w-none md:max-w-none h-[95vh] flex flex-col p-3 translate-x-0 translate-y-0 [&>button]:hidden"
          style={{
            left: sidebarWidth,
            width: `calc(100vw - ${sidebarWidth}px)`,
            top: "2.5vh",
            maxWidth: "calc(100vw - 2rem)",
          }}
        >
          <div className="flex items-center justify-between gap-2 pb-2 border-b pr-1">
            <h3 className="text-sm font-semibold">Cashflow Forecast · Full Screen</h3>
            <div className="flex items-center gap-1">
              <CashflowGlossarySheet />
              <Button variant="outline" size="sm" onClick={handleExport} className="gap-1 h-8 px-2" disabled={loading || rows.length === 0}>
                <Download className="h-3.5 w-3.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => setExpanded(false)} className="gap-1 h-8 px-2">
                <Minimize2 className="h-3.5 w-3.5" /> Close
              </Button>
            </div>
          </div>
          <div className="flex-1 min-h-0 min-w-0 mt-2 overflow-hidden">
            {loading ? (
              <div className="flex h-full items-center justify-center text-muted-foreground gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading cashflow forecast…
              </div>
            ) : error ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
                <p className="text-destructive">{error}</p>
                <Button variant="outline" size="sm" onClick={() => void loadCashflow()}>
                  Retry
                </Button>
              </div>
            ) : (
              <TableInner rows={rows} totals={totals} variant={variant} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CashflowForecastTable;
